import { supabase } from './supabase.js'
import { currentMonthKey, shiftMonthKey } from './format.js'

/**
 * Reports, rebuilt around the five things his Apps Script version gets
 * structurally wrong:
 *
 *   1. It charts salary against paid. The cut that matters is MOB against SWB,
 *      and savings rate over time is the headline.
 *   2. Petty cash ignores pending, which flatters him mid-month. So every
 *      figure here comes in two: paid, and planned.
 *   3. There is no month summary anywhere. So there is one now.
 *   4. There is no way to follow one line across months. So a per-line trend
 *      exists, built from payable_history, which the app already writes when
 *      he edits an amount — nothing extra for him to type.
 *   5. There are no category totals. So there are.
 */

export function lastMonths(n, end = currentMonthKey()) {
  return Array.from({ length: n }, (_, i) => shiftMonthKey(end, -(n - 1 - i)))
}

function rangeOf(months) {
  const first = months[0]
  const last = months[months.length - 1]
  const [y, m] = last.split('-').map(Number)
  const lastDay = new Date(y, m, 0).getDate()
  return [first + '-01', last + '-' + String(lastDay).padStart(2, '0')]
}

/**
 * Everything the Reports screen needs, in four queries rather than one per
 * month. Payables are fetched once and bucketed in memory.
 */
export async function getReport(months) {
  const [from, to] = rangeOf(months)

  const [cats, payouts, income] = await Promise.all([
    supabase.from('payout_categories').select('*').order('sort_order'),
    supabase.from('payouts').select('*').in('month_key', months),
    supabase.from('income_entries').select('amount, received_on')
      .gte('received_on', from).lte('received_on', to),
  ])
  if (cats.error) throw cats.error
  if (payouts.error) throw payouts.error
  if (income.error) throw income.error

  const payoutRows = payouts.data ?? []
  const ids = payoutRows.map((p) => p.id)

  let payables = []
  if (ids.length) {
    const { data, error } = await supabase.from('payables').select('*').in('payout_id', ids)
    if (error) throw error
    payables = data ?? []
  }

  const catById = Object.fromEntries((cats.data ?? []).map((c) => [c.id, c]))
  const monthOfPayout = Object.fromEntries(payoutRows.map((p) => [p.id, p.month_key]))

  /* Income by month, from the date it arrived. */
  const incomeByMonth = {}
  for (const r of income.data ?? []) {
    const key = r.received_on.slice(0, 7)
    incomeByMonth[key] = (incomeByMonth[key] || 0) + Number(r.amount || 0)
  }

  const byMonth = months.map((key) => {
    const mine = payables.filter((p) => monthOfPayout[p.payout_id] === key)

    let mobPaid = 0, mobPlanned = 0, swbPaid = 0, swbPlanned = 0
    for (const p of mine) {
      const amt = Number(p.amount || 0)
      const build = catById[p.category_id]?.kind === 'build'
      if (build) {
        swbPlanned += amt
        if (p.status === 'Paid') swbPaid += amt
      } else {
        mobPlanned += amt
        if (p.status === 'Paid') mobPaid += amt
      }
    }

    const inTotal = incomeByMonth[key] || 0
    return {
      key,
      in: inTotal,
      mobPaid, mobPlanned, swbPaid, swbPlanned,
      paid: mobPaid + swbPaid,
      planned: mobPlanned + swbPlanned,
      leftPaid: inTotal - (mobPaid + swbPaid),
      leftPlanned: inTotal - (mobPlanned + swbPlanned),
      /* Two rates, for the same reason there are two of everything else:
         one is where the month stands now, one is where it is headed. */
      ratePaid: inTotal > 0 ? Math.round((swbPaid / inTotal) * 100) : 0,
      ratePlanned: inTotal > 0 ? Math.round((swbPlanned / inTotal) * 100) : 0,
      count: mine.length,
    }
  })

  /* Category totals across the whole range, paid and planned. */
  const byCategory = {}
  for (const p of payables) {
    const c = catById[p.category_id]
    const id = c?.id ?? 'none'
    const amt = Number(p.amount || 0)
    if (!byCategory[id]) {
      byCategory[id] = {
        id,
        name: c?.name ?? 'Uncategorised',
        code: c?.code ?? '',
        kind: c?.kind ?? 'spend',
        paid: 0,
        planned: 0,
        count: 0,
      }
    }
    byCategory[id].planned += amt
    byCategory[id].count += 1
    if (p.status === 'Paid') byCategory[id].paid += amt
  }

  /* Distinct payable names, for the per-line trend picker. */
  const names = Array.from(new Set(payables.map((p) => p.name))).sort((a, b) =>
    a.localeCompare(b),
  )

  return {
    months,
    byMonth,
    byCategory: Object.values(byCategory).sort((a, b) => b.planned - a.planned),
    names,
    payables,
    monthOfPayout,
    catById,
  }
}

/**
 * One line followed across months — the grocery bill creeping from ₱10,000 to
 * ₱13,900. Built from the payables themselves, so it works from day one and
 * needs no extra input.
 */
export function trendFor(report, name) {
  return report.months.map((key) => {
    const rows = report.payables.filter(
      (p) => p.name === name && report.monthOfPayout[p.payout_id] === key,
    )
    return {
      key,
      amount: rows.reduce((s, p) => s + Number(p.amount || 0), 0),
      present: rows.length > 0,
    }
  })
}

/** Recorded edits to one line, so a change of amount has a date against it. */
export async function historyFor(name) {
  const { data: payables, error } = await supabase.from('payables').select('id').eq('name', name)
  if (error) throw error
  const ids = (payables ?? []).map((p) => p.id)
  if (!ids.length) return []

  const { data, error: e2 } = await supabase
    .from('payable_history')
    .select('*')
    .in('payable_id', ids)
    .eq('field', 'amount')
    .order('created_at', { ascending: false })
    .limit(20)
  if (e2) throw e2
  return data ?? []
}

export function toCsv(report) {
  const esc = (v) => {
    const s = v == null ? '' : String(v)
    return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s
  }
  const lines = [
    ['Month', 'In', 'MOB paid', 'MOB planned', 'SWB paid', 'SWB planned',
     'Left after paid', 'Left after all', 'Rate paid %', 'Rate planned %'].join(','),
  ]
  for (const m of report.byMonth) {
    lines.push([
      m.key, m.in, m.mobPaid, m.mobPlanned, m.swbPaid, m.swbPlanned,
      m.leftPaid, m.leftPlanned, m.ratePaid, m.ratePlanned,
    ].map(esc).join(','))
  }
  lines.push('')
  lines.push(['Category', 'Kind', 'Paid', 'Planned', 'Lines'].join(','))
  for (const c of report.byCategory) {
    lines.push([c.name, c.kind, c.paid, c.planned, c.count].map(esc).join(','))
  }
  return lines.join('\n')
}
